import { call, put, takeLatest, delay } from 'redux-saga/effects';
import request from 'utils/request';
import { getUserTimezone } from 'utils/time';
import { actions } from './index';
import {
  AlertsAndNotificationsResponse,
  MomentNotificationsResponse
} from '../types';

/** Waits between re-reads while a moment's notification sync is still STALE (bounded ~30s) */
const STALE_RETRY_DELAYS_MS = [500, 1000, 1500, 2000, 5000, 10000, 10000];

/** Request init for GET /notifications and GET /notifications/moment/{issueId} only */
function notificationRequestInit(): RequestInit {
  return {
    headers: {
      'X-Timezone': getUserTimezone()
    }
  };
}

function* loadAlertsAndNotifications() {
  try {
    const url = `${process.env.REACT_APP_API_V2_URL}/notifications`;
    //const url = `/notifications.json`;
    const response: AlertsAndNotificationsResponse = yield call(
      request,
      url,
      notificationRequestInit()
    );
    yield put(actions.alertsAndNotificationsLoaded(response));
  } catch (err: any) {
    console.error('Error loading alerts and notifications:', err);
    yield put(actions.alertsAndNotificationsError(err));
  }
}

function* loadMomentNotifications(
  action: ReturnType<typeof actions.loadMomentNotifications>
) {
  const { issueId } = action.payload;
  try {
    const url = `${process.env.REACT_APP_API_V2_URL}/notifications/moment/${issueId}`;
    let response: MomentNotificationsResponse = yield call(
      request,
      url,
      notificationRequestInit()
    );

    // The backend sync is async: re-read a bounded number of times until it settles
    for (
      let attempt = 0;
      response.syncStatus === 'STALE' && attempt < STALE_RETRY_DELAYS_MS.length;
      attempt += 1
    ) {
      yield delay(STALE_RETRY_DELAYS_MS[attempt]);
      response = yield call(request, url, notificationRequestInit());
    }

    if (response.syncStatus === 'STALE') {
      const error = new Error(
        `Notifications still STALE for ${issueId} after ${STALE_RETRY_DELAYS_MS.length} retries.`
      );
      console.error(error.message);
      yield put(
        actions.momentNotificationsError({
          issueId,
          error
        })
      );
      return;
    }

    yield put(
      actions.momentNotificationsLoaded({
        issueId,
        response
      })
    );
  } catch (err: any) {
    console.error(`Error loading moment notifications for ${issueId}:`, err);
    yield put(
      actions.momentNotificationsError({
        issueId,
        error: err
      })
    );
  }
}

export function* alertsAndNotificationsSaga() {
  yield takeLatest(
    actions.loadAlertsAndNotifications.type,
    loadAlertsAndNotifications
  );
  yield takeLatest(
    actions.loadMomentNotifications.type,
    loadMomentNotifications
  );
}
